import React, { useState } from 'react';
import { Camera, PawPrint, ArrowLeft, X, ImagePlus, Dog, ArrowRight, Eye } from "lucide-react";
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import BackButton from './BackButton';

const PhotoUploadView = ({
    handleBack,
    currentPetName,
    files,
    removeFile,
    handleFileUpload,
    handleSubmitPhotos
}) => {
    // Always use 3-column grid on mobile regardless of image count
    const gridColumnsClass = "grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4";
    
    // State for photo preview
    const [previewPhoto, setPreviewPhoto] = useState(null);
    
    return (
        <motion.div
            key="photos"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="relative flex flex-col h-full"
        >
            {/* Header */}
            <div className="relative p-4 sm:p-6 lg:p-8 text-center border-b bg-gradient-to-r from-amber-100/50 via-amber-50/50 to-amber-100/50">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-1 bg-gradient-to-r from-amber-300 to-amber-500 rounded-b-lg" />
                <BackButton onClick={handleBack} />

                <div className="mx-auto mb-3 sm:mb-4 relative">
                    <motion.div
                        initial={{ rotate: 0 }}
                        animate={{ rotate: [0, 12, 0] }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                        className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl sm:rounded-2xl shadow-lg flex items-center justify-center relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                        <Camera className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white drop-shadow-md" />
                    </motion.div>
                    <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -bottom-2 right-1/2 transform translate-x-10"
                    >
                        <PawPrint className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-amber-600" />
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-2 sm:space-y-3"
                >
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-amber-900 px-2">
                        {files.length === 0
                            ? currentPetName
                                ? `¡Hora de tomar fotos de ${currentPetName}!`
                                : "¡Hora de tomar fotos de tu perrito!"
                            : currentPetName
                                ? `¡Genial! Ya tenemos ${files.length} ${files.length === 1 ? 'foto' : 'fotos'} de ${currentPetName}`
                                : `¡Genial! Ya tenemos ${files.length} ${files.length === 1 ? 'foto' : 'fotos'} de tu perrito`
                        }
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-amber-700 max-w-md mx-auto px-3">
                        Para un mejor análisis, incluye fotos de cuerpo completo desde diferentes ángulos
                    </p>
                </motion.div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 md:p-8 pb-24 sm:pb-6">
                <div className="space-y-4 sm:space-y-6">
                    {/* Progress Bar */}
                    <div className="bg-white/50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-amber-100 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                            <div className="flex items-center gap-2 sm:gap-3">
                                <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                                    <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm sm:text-base font-medium text-gray-800">Progreso del análisis</h3>
                                    <p className="text-xs sm:text-sm text-amber-600">
                                        {files.length === 0 ? 'Agrega hasta 5 fotos' : `${5 - files.length} fotos restantes`}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3 ml-8 sm:ml-0">
                                <span className="text-xs sm:text-sm font-medium text-amber-700">
                                    {files.length}/5
                                </span>
                                <div className="h-2 sm:h-2.5 w-16 sm:w-20 bg-amber-100 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(files.length / 5) * 100}%` }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Photo Grid - Always 3 columns on mobile */}
                    <div className={gridColumnsClass}>
                        {files.map((file, index) => (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                whileHover={{ scale: 1.02 }}
                                key={`photo-${index}`}
                                className="relative aspect-square group"
                            >
                                <div 
                                    className="absolute inset-0 rounded-lg sm:rounded-xl overflow-hidden border-2 border-amber-100 group-hover:border-amber-300 transition-all shadow-sm group-hover:shadow-md"
                                    onClick={() => setPreviewPhoto(file)}
                                >
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt={`Foto ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                    
                                    {/* Preview indicator */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="bg-white/80 rounded-full p-1">
                                            <Eye className="w-4 h-4 text-amber-600" />
                                        </div>
                                    </div>
                                </div>

                                {/* Always visible delete button */}
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 p-1.5 sm:p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg z-10"
                                >
                                    <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                </motion.button>
                            </motion.div>
                        ))}

                        {files.length < 5 && (
                            <motion.label
                                whileHover={{ scale: 1.02 }}
                                animate={files.length === 0 ? {
                                    boxShadow: [
                                        "0 0 0 0 rgba(251, 191, 36, 0)",
                                        "0 0 0 4px rgba(251, 191, 36, 0.2)",
                                        "0 0 0 0 rgba(251, 191, 36, 0)"
                                    ]
                                } : {}}
                                transition={files.length === 0 ? {
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                } : {}}
                                className="relative aspect-square cursor-pointer group"
                            >
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleFileUpload}
                                    className="absolute inset-0 opacity-0"
                                />
                                <div className={cn(
                                    "absolute inset-0 flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all",
                                    "bg-gradient-to-br from-amber-50/50 to-amber-100/30",
                                    files.length === 0
                                        ? "border-amber-300 animate-subtle-bounce shadow-lg"
                                        : "border-amber-200 group-hover:border-amber-400 group-hover:shadow-md"
                                )}>
                                    <div className={cn(
                                        "p-2 sm:p-3 rounded-xl transition-colors",
                                        files.length === 0
                                            ? "bg-amber-100"
                                            : "bg-amber-50 group-hover:bg-amber-100"
                                    )}>
                                        <ImagePlus className={cn(
                                            "w-5 h-5 sm:w-6 sm:h-6 transition-colors",
                                            files.length === 0
                                                ? "text-amber-600 animate-subtle-pulse"
                                                : "text-amber-500 group-hover:text-amber-600"
                                        )} />
                                    </div>
                                    <p className="mt-2 sm:mt-3 text-xs sm:text-sm font-medium text-amber-800 text-center px-1 sm:px-2">
                                        {files.length === 0 ? 'Sube las fotos' : 'Agregar más'}
                                    </p>
                                    <p className="text-xs sm:text-xs text-amber-600/80 mt-0.5 sm:mt-1">
                                        {files.length === 0 ? 'Máximo 5 fotos' : `${5 - files.length} restantes`}
                                    </p>
                                </div>
                            </motion.label>
                        )}
                    </div>

                    {/* Desktop Button */}
                    <div className="hidden sm:block">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleSubmitPhotos}
                            disabled={files.length === 0}
                            className={cn(
                                "w-full p-3 sm:p-4 rounded-xl font-medium",
                                "transition-all duration-200",
                                "flex items-center justify-center gap-2 sm:gap-3",
                                "relative overflow-hidden",
                                files.length > 0
                                    ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md hover:shadow-lg"
                                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            )}
                        >
                            {files.length > 0 && (
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 animate-shimmer" />
                            )}
                            <Dog className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="text-base sm:text-lg">Continuar</span>
                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* Mobile Button */}
            <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white/95 to-white/80">
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmitPhotos}
                    disabled={files.length === 0}
                    className={cn(
                        "w-full p-3 rounded-xl font-medium",
                        "transition-all duration-200",
                        "flex items-center justify-center gap-2",
                        "relative overflow-hidden",
                        files.length > 0
                            ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md hover:shadow-lg"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    )}
                >
                    {files.length > 0 && (
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 animate-shimmer" />
                    )}
                    <Dog className="w-4 h-4" />
                    <span className="text-base">Continuar</span>
                    <ArrowRight className="w-4 h-4" />
                </motion.button>
            </div>

            {/* Photo Preview Modal */}
            <AnimatePresence mode="sync">
                {previewPhoto && (
                    <div 
                        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" 
                        onClick={() => setPreviewPhoto(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="relative max-w-3xl max-h-[80vh] w-full bg-white rounded-xl overflow-hidden shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="absolute top-2 right-2 z-10">
                                <button 
                                    onClick={() => setPreviewPhoto(null)}
                                    className="bg-white/80 p-2 rounded-full hover:bg-white transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-800" />
                                </button>
                            </div>
                            <div className="h-full max-h-[80vh] overflow-hidden">
                                <img
                                    src={URL.createObjectURL(previewPhoto)}
                                    alt="Vista previa"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default PhotoUploadView;